const DEFAULT_BASE_PATH = '/mediagw';
// Clamp a resize dimension to a positive integer the proxy accepts (rejects 0,
// negatives and non-finite). No-op for real sizes, which are always >= 1.
const dim = (n) => (Number.isFinite(n) ? Math.max(1, Math.round(n)) : 1);
// Percent-encode only the comma (the intra-segment parameter separator). `_`, `:` (ar_16:9)
// and `.` in a transform segment must stay literal, so this is a targeted replace rather
// than encodeURIComponent.
const encodeSeparatorCommas = (segment) => segment.replace(/,/g, '%2C');
const ResizeFunctions = {
    contain: 'c_fit',
    limit: 'c_limit',
    cover: 'c_fill',
};
/**
 * Fluent builder for the mediagw transform URL grammar (Cloudinary-style),
 * shared by webng (`/mediagw/…`) and nxt (`/nxt/mediagw/…`) — the mount prefix
 * is the only thing that differs, so it is a constructor parameter. The source
 * is a plain string; products that reference media by a typed handle (e.g. nxt's
 * ArtifactReference) normalize it to a string before calling `src`/`image`.
 *
 * Ported from nxt's @nxt/media-utils MediaGw. Filter order is significant and
 * threaded by the proxy, so callers emit rotate → user-crop → resize → cover-crop.
 */
export class MediaGw {
    constructor(method = 'image', transforms = [], sourceUrl, basePath = DEFAULT_BASE_PATH) {
        this.method = method;
        this.transforms = transforms;
        this.sourceUrl = sourceUrl;
        this.basePath = basePath;
    }
    clone() {
        return new MediaGw(this.method, [...this.transforms], this.sourceUrl, this.basePath);
    }
    static image(url, basePath = DEFAULT_BASE_PATH) {
        const builder = new MediaGw('image', [], undefined, basePath);
        return url ? builder.src(url) : builder;
    }
    static metadata(url, basePath = DEFAULT_BASE_PATH) {
        const builder = new MediaGw('metadata', [], undefined, basePath);
        return url ? builder.src(url) : builder;
    }
    static download(url, basePath = DEFAULT_BASE_PATH) {
        const builder = new MediaGw('download', [], undefined, basePath);
        return url ? builder.src(url) : builder;
    }
    src(url) {
        this.sourceUrl = url;
        return this;
    }
    transform(...parts) {
        const filtered = parts.filter(Boolean);
        if (filtered.length > 0) {
            this.transforms.push(filtered.join(','));
        }
        return this;
    }
    angle(angle) {
        // Reject non-finite values before interpolating into the proxy path segment.
        if (!Number.isFinite(angle))
            return this;
        return this.transform(`a_${angle}`);
    }
    crop(x, y, width, height) {
        if (![x, y, width, height].every(Number.isFinite))
            return this;
        return this.transform('c_crop', `x_${x.toFixed(0)}`, `y_${y.toFixed(0)}`, `w_${width.toFixed(0)}`, `h_${height.toFixed(0)}`);
    }
    cropPct(x, y, width, height) {
        if (![x, y, width, height].every(Number.isFinite))
            return this;
        return this.transform('c_crop', `x_${x.toFixed(5)}`, `y_${y.toFixed(5)}`, `w_${width.toFixed(5)}`, `h_${height.toFixed(5)}`);
    }
    /** Emit the rotate → user-crop part of the chain from a stored transform. */
    applyTransform(transform) {
        if (transform === null || transform === void 0 ? void 0 : transform.rotate) {
            this.angle(transform.rotate);
        }
        if (transform === null || transform === void 0 ? void 0 : transform.crop) {
            this.cropPct(transform.crop.x, transform.crop.y, transform.crop.width, transform.crop.height);
        }
        return this;
    }
    resize(width, height, options = {}) {
        // Guard the non-positive/non-finite dimension class: the proxy rejects w_0/h_0
        // (and negatives), which a degenerate transform can otherwise produce. A no-op
        // for real resize targets, which are always >= 1.
        return this.transform(options.fit ? ResizeFunctions[options.fit] : 'c_fill', `w_${dim(width)}`, `h_${dim(height)}`);
    }
    contain(width, height) {
        return this.resize(width, height, { fit: 'contain' });
    }
    cover(width, height) {
        return this.resize(width, height, { fit: 'cover' });
    }
    limit(width, height) {
        return this.resize(width, height, { fit: 'limit' });
    }
    width(width) {
        return this.transform(`w_${dim(width)}`);
    }
    height(height) {
        return this.transform(`h_${dim(height)}`);
    }
    aspectRatio(width, height) {
        return this.transform(`ar_${dim(width)}:${dim(height)}`);
    }
    gravity(gravity) {
        return this.transform(`g_${gravity}`);
    }
    kernel(kernel) {
        return this.transform(`tikk_${kernel}`);
    }
    format(format) {
        this.transforms.push(`f_${format}`);
        return this;
    }
    /**
     * @param base optional base URL; when set, the result is absolute on that
     *             base's ORIGIN, otherwise it is a root-relative path. Only the
     *             origin is used and the path is concatenated verbatim —
     *             deliberately not `new URL(path, base)`, because the path carries
     *             a whole source URL in its tail and the URL parser would re-encode
     *             it (` ` → `%20`), addressing a different upstream.
     */
    build(base) {
        if (!this.sourceUrl) {
            throw new Error('MediaGw: source URL is required');
        }
        // Encode the intra-segment parameter comma as %2C so our transform grammar no longer
        // trips HTML srcset parsers: non-conformant ones (Bingbot, rehype, SvelteKit, pywb, …)
        // split the attribute on every comma, which used to fragment a multi-parameter segment.
        // The proxy decodes %2C back to a separator. Only transform segments are encoded; the
        // "/" chain separators and the appended source URL stay raw. A source reference that
        // itself contains a comma (e.g. a data: payload) would still be splittable — but those
        // are passed through untouched upstream and never emitted as srcset candidates; real
        // srcset sources (imageservice://, https:// media) are comma-free.
        const transformPath = this.transforms.length > 0 ? `/${this.transforms.map(encodeSeparatorCommas).join('/')}` : '';
        const path = `${this.basePath}/${this.method}${transformPath}/${this.sourceUrl}`;
        return base ? new URL(base).origin + path : path;
    }
}
